import { ConfirmDialog } from '@/components/dialog/confirm-dialog';
import { placeCaretFromFieldGutter } from '@/components/editor/lexical/field-gutter-caret';
import InlineVariableEditor from '@/components/editor/lexical/inline-variable-editor';
import { Field } from '@/components/ui/field';
import { toaster } from '@/components/ui/toaster';
import {
  usePreviewProgramTracking,
  useProgramPostbackCredential,
  useUpdateProgramTracking,
} from '@/hooks/actions/use-program-tracking-actions';
import { useProgramTrackingQuery } from '@/hooks/queries/use-program-tracking-query';
import { useDebounce } from '@/hooks/useDebounce';
import { writeClipboardText } from '@/libs/clipboard';
import { Box, Button, Card, Flex, Input, Skeleton, Text, Textarea, VStack } from '@chakra-ui/react';
import { TIMEONE_TRACKING_MACROS, type ProgramTrackingPreview } from '@fluenceur/types';
import { type AvailableVariable, VariableDropdownPlugin } from '@fluenceur/ui';
import { Copy, EditPencil, Eye, EyeClosed } from 'iconoir-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ProgramTrackingCardProps {
  brandId: string;
  programId: string;
}

interface TrackingDraft {
  standardTemplate: string;
  deepLinkTemplate: string;
  origins: string;
  destinationUrl: string;
}

const emptyDraft: TrackingDraft = {
  standardTemplate: '',
  deepLinkTemplate: '',
  origins: '',
  destinationUrl: '',
};

interface TrackingTemplateEditorProps {
  ariaLabel: string;
  editorKey: number;
  helperText: string;
  insertVariableLabel: string;
  label: string;
  onChange: (value: string) => void;
  placeholder: string;
  value: string;
  variables: AvailableVariable[];
}

const STANDARD_TEMPLATE_PLACEHOLDER =
  'https://tracking.publicidees.com/clic.php?partid={{partid}}&progid=12345&cb={{affiliateId}}';
const DEEP_LINK_TEMPLATE_PLACEHOLDER = `${STANDARD_TEMPLATE_PLACEHOLDER}&dpl={{destinationUrl}}`;

const TrackingTemplateEditor = ({
  ariaLabel,
  editorKey,
  helperText,
  insertVariableLabel,
  label,
  onChange,
  placeholder,
  value,
  variables,
}: TrackingTemplateEditorProps) => (
  <Field label={label} helperText={helperText} width="full" minWidth={0}>
    <Box
      width="full"
      minWidth={0}
      borderWidth="1px"
      borderColor="border"
      rounded="lg"
      bg="bg"
      px={3}
      py={2}
      fontFamily="mono"
      transition="border-color 0.2s, box-shadow 0.2s"
      _focusWithin={{
        borderColor: 'border.emphasized',
        boxShadow: '0 0 0 1px var(--chakra-colors-border-emphasized)',
      }}
      onMouseDown={placeCaretFromFieldGutter}
    >
      <InlineVariableEditor
        editorKey={editorKey}
        initialPlainText={value}
        ariaLabel={ariaLabel}
        availableVariables={variables}
        onContentChange={onChange}
        placeholder={placeholder}
        maxLength={2048}
        minH="36px"
      >
        <VariableDropdownPlugin
          availableVariables={variables}
          compact
          insertVariableLabel={insertVariableLabel}
          insertTrailingSpace={false}
          placement="bottom-end"
        />
      </InlineVariableEditor>
    </Box>
  </Field>
);

const ProgramTrackingCard = ({ brandId, programId }: ProgramTrackingCardProps) => {
  const { t } = useTranslation();
  const {
    data: settings,
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useProgramTrackingQuery(brandId, programId);
  const previewMutation = usePreviewProgramTracking(programId, { notifyOnError: false });
  const previewTracking = previewMutation.mutateAsync;
  const updateMutation = useUpdateProgramTracking(brandId, programId);
  const credentialMutation = useProgramPostbackCredential(brandId, programId);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<TrackingDraft>(emptyDraft);
  const [preview, setPreview] = useState<ProgramTrackingPreview | null>(null);
  const [previewError, setPreviewError] = useState(false);
  const [previewUpdating, setPreviewUpdating] = useState(false);
  const [confirmingProgramIdChange, setConfirmingProgramIdChange] = useState(false);
  const [postbackUrl, setPostbackUrl] = useState<string | null>(null);
  const [postbackVisible, setPostbackVisible] = useState(false);
  const previewRequest = useRef(0);
  const currentScope = useRef(`${brandId}:${programId}`);
  currentScope.current = `${brandId}:${programId}`;

  useEffect(() => {
    setEditing(false);
    setPreview(null);
    setPreviewError(false);
    setPreviewUpdating(false);
    setConfirmingProgramIdChange(false);
    setPostbackUrl(null);
    setPostbackVisible(false);
    previewRequest.current += 1;
  }, [brandId, programId]);

  const startEditing = () => {
    setDraft({
      standardTemplate: settings?.config?.standardTemplate ?? '',
      deepLinkTemplate: settings?.config?.deepLinkTemplate ?? '',
      origins: settings?.config?.allowedDestinationOrigins.join('\n') ?? '',
      destinationUrl: '',
    });
    setPreview(null);
    setPreviewError(false);
    setPreviewUpdating(true);
    setEditing(true);
  };

  const allowedDestinationOrigins = useMemo(
    () =>
      draft.origins
        .split(/[\n,]/)
        .map((origin) => origin.trim())
        .filter(Boolean),
    [draft.origins]
  );

  const standardTemplateVariables = useMemo<AvailableVariable[]>(
    () => [
      {
        key: 'partid',
        label: t('programs.programOverview.tracking.variables.partid'),
        value: TIMEONE_TRACKING_MACROS.partid,
      },
      {
        key: 'affiliateId',
        label: t('programs.programOverview.tracking.variables.affiliateId'),
        value: TIMEONE_TRACKING_MACROS.affiliateId,
      },
    ],
    [t]
  );
  const deepLinkTemplateVariables = useMemo<AvailableVariable[]>(
    () => [
      ...standardTemplateVariables,
      {
        key: 'destinationUrl',
        label: t('programs.programOverview.tracking.variables.destinationUrl'),
        value: TIMEONE_TRACKING_MACROS.destinationUrl,
      },
    ],
    [standardTemplateVariables, t]
  );

  const input = useMemo(
    () => ({
      expectedRevision: settings?.config?.revision ?? 0,
      standardTemplate: draft.standardTemplate,
      deepLinkTemplate: draft.deepLinkTemplate || null,
      allowedDestinationOrigins,
    }),
    [
      allowedDestinationOrigins,
      draft.deepLinkTemplate,
      draft.standardTemplate,
      settings?.config?.revision,
    ]
  );
  const previewInput = useMemo(
    () => ({
      ...input,
      affiliateId: 'PREVIEW-AFFILIATE',
      destinationUrl: draft.destinationUrl || null,
    }),
    [draft.destinationUrl, input]
  );
  const debouncedPreviewInput = useDebounce(previewInput, 450);

  const updateDraft = (key: keyof TrackingDraft, value: string) => {
    previewRequest.current += 1;
    setPreview(null);
    setPreviewError(false);
    setPreviewUpdating(true);
    setDraft((current) => ({ ...current, [key]: value }));
  };

  useEffect(() => {
    if (!editing || debouncedPreviewInput !== previewInput) return;
    const requestId = ++previewRequest.current;
    setPreviewUpdating(true);
    void previewTracking(debouncedPreviewInput)
      .then((result) => {
        if (previewRequest.current !== requestId) return;
        setPreview(result);
        setPreviewError(false);
      })
      .catch(() => {
        if (previewRequest.current !== requestId) return;
        setPreview(null);
        setPreviewError(true);
      })
      .finally(() => {
        if (previewRequest.current === requestId) setPreviewUpdating(false);
      });
  }, [debouncedPreviewInput, editing, previewInput, previewTracking]);

  const save = async (confirmedPreviousExternalProgramId?: string) => {
    const requestedScope = `${brandId}:${programId}`;
    try {
      const updated = await updateMutation.mutateAsync({
        ...input,
        ...(confirmedPreviousExternalProgramId ? { confirmedPreviousExternalProgramId } : {}),
      });
      if (currentScope.current !== requestedScope) return;
      setPreview(null);
      setEditing(false);
      setConfirmingProgramIdChange(false);

      try {
        const url = await fetchPostbackUrl(updated.externalProgramId);
        if (url) {
          setPostbackUrl(url);
          setPostbackVisible(true);
        }
      } catch {
        // The tracking configuration is already saved. The mutation hook
        // reports the credential error and the URL can be requested again.
      }
    } catch {
      // Keep the draft intact so the user can resolve a validation or version conflict.
    }
  };

  const handleSave = () => {
    if (preview?.requiresProgramIdChangeConfirmation) {
      setConfirmingProgramIdChange(true);
      return;
    }
    void save();
  };

  const fetchPostbackUrl = async (externalProgramId = settings?.externalProgramId) => {
    const requestedScope = `${brandId}:${programId}`;
    try {
      const credential = await credentialMutation.mutateAsync();
      if (
        currentScope.current !== requestedScope ||
        credential.programId !== programId ||
        credential.externalProgramId !== externalProgramId
      ) {
        return null;
      }
      return credential.url;
    } catch {
      // Mutation hook surfaces the translated error toast.
      return null;
    } finally {
      credentialMutation.reset();
    }
  };

  const togglePostbackVisibility = async () => {
    if (postbackVisible) {
      setPostbackVisible(false);
      setPostbackUrl(null);
      return;
    }
    const url = await fetchPostbackUrl();
    if (url) {
      setPostbackUrl(url);
      setPostbackVisible(true);
    }
  };

  const copyCredential = async () => {
    const url = postbackUrl ?? (await fetchPostbackUrl());
    if (!url) return;
    try {
      if (await writeClipboardText(url)) {
        toaster.success({
          title: t('programs.programOverview.tracking.copied'),
        });
      } else {
        toaster.error({ title: t('programs.programOverview.tracking.copyError') });
      }
    } catch {
      // Mutation hook surfaces the translated error toast.
    }
  };

  if (isLoading) return <Skeleton height="220px" width="full" />;
  if (isError) {
    return (
      <Card.Root width="full" padding={8}>
        <Card.Body padding={0} gap={4}>
          <Text role="alert">{t('programs.programOverview.tracking.loadError')}</Text>
          <Button
            alignSelf="start"
            variant="outline"
            loading={isFetching}
            onClick={() => void refetch()}
          >
            {t('common.retry')}
          </Button>
        </Card.Body>
      </Card.Root>
    );
  }
  // The API resolves partid from the organization's TimeOne integration. The
  // program ID can be assigned by saving the first literal progid template.
  if (!settings?.integrationPartid) return null;

  return (
    <Card.Root width="full" padding={8}>
      <Card.Header padding={0}>
        <Flex justify="space-between" align="start" gap={4}>
          <VStack align="start" gap={1}>
            <Card.Title fontSize="2xl">{t('programs.programOverview.tracking.title')}</Card.Title>
            <Card.Description>
              {t('programs.programOverview.tracking.description')}
            </Card.Description>
          </VStack>
          {settings.canManage && !editing && (
            <Button variant="outline" size="sm" onClick={startEditing}>
              <EditPencil width={18} height={18} />
              {t('common.edit')}
            </Button>
          )}
        </Flex>
      </Card.Header>
      <Card.Body padding={0} marginTop={6}>
        {editing ? (
          <VStack align="stretch" gap={4}>
            <TrackingTemplateEditor
              ariaLabel={t('programs.programOverview.tracking.standardTemplate')}
              editorKey={1}
              helperText={t('programs.programOverview.tracking.standardTemplateHint')}
              insertVariableLabel={t('programs.programOverview.tracking.insertVariable')}
              label={t('programs.programOverview.tracking.standardTemplate')}
              onChange={(value) => updateDraft('standardTemplate', value)}
              placeholder={STANDARD_TEMPLATE_PLACEHOLDER}
              value={draft.standardTemplate}
              variables={standardTemplateVariables}
            />
            <TrackingTemplateEditor
              ariaLabel={t('programs.programOverview.tracking.deepLinkTemplate')}
              editorKey={2}
              helperText={t('programs.programOverview.tracking.deepLinkTemplateHint')}
              insertVariableLabel={t('programs.programOverview.tracking.insertVariable')}
              label={t('programs.programOverview.tracking.deepLinkTemplate')}
              onChange={(value) => updateDraft('deepLinkTemplate', value)}
              placeholder={DEEP_LINK_TEMPLATE_PLACEHOLDER}
              value={draft.deepLinkTemplate}
              variables={deepLinkTemplateVariables}
            />
            <label>
              <Text fontSize="sm" fontWeight="medium" marginBottom={1}>
                {t('programs.programOverview.tracking.allowedOrigins')}
              </Text>
              <Textarea
                value={draft.origins}
                onChange={(event) => updateDraft('origins', event.target.value)}
                placeholder="https://www.example.com"
              />
            </label>
            <label>
              <Text fontSize="sm" fontWeight="medium" marginBottom={1}>
                {t('programs.programOverview.tracking.previewDestination')}
              </Text>
              <Input
                value={draft.destinationUrl}
                onChange={(event) => updateDraft('destinationUrl', event.target.value)}
                placeholder={allowedDestinationOrigins[0]}
              />
            </label>
            <Box
              borderWidth="1px"
              borderColor="border"
              borderRadius="lg"
              bg="bg.subtle"
              p={3}
              aria-live="polite"
            >
              <Text fontSize="xs" color="fg.muted" fontWeight="medium" mb={1}>
                {t('programs.programOverview.tracking.livePreview')}
              </Text>
              {previewUpdating ? (
                <Text fontSize="sm" color="fg.muted">
                  {t('programs.programOverview.tracking.updatingPreview')}
                </Text>
              ) : previewError ? (
                <Text fontSize="sm" color="red.fg">
                  {t('programs.programOverview.tracking.previewError')}
                </Text>
              ) : (
                <Text
                  fontFamily="mono"
                  fontSize="sm"
                  overflowWrap="anywhere"
                  data-testid="tracking-preview"
                >
                  {preview?.url ?? '—'}
                </Text>
              )}
            </Box>
            <Flex gap={2} wrap="wrap">
              <Button
                onClick={handleSave}
                loading={updateMutation.isPending}
                disabled={!preview || previewUpdating || previewError}
              >
                {t('common.save')}
              </Button>
              <Button variant="ghost" onClick={() => setEditing(false)}>
                {t('common.cancel')}
              </Button>
            </Flex>
          </VStack>
        ) : (
          <VStack align="stretch" gap={4}>
            <Text fontSize="sm">
              {settings.config
                ? t('programs.programOverview.tracking.configured', {
                    revision: settings.config.revision,
                  })
                : t('programs.programOverview.tracking.notConfigured')}
            </Text>
            <Text fontSize="sm">
              {t('programs.programOverview.tracking.integrationPartid', {
                partid: settings.integrationPartid ?? '—',
              })}
            </Text>
            <Text fontSize="sm">
              {t('programs.programOverview.tracking.externalProgramId', {
                programId:
                  settings.externalProgramId ??
                  t('programs.programOverview.tracking.externalProgramIdMissing'),
              })}
            </Text>
            {settings.canManage && settings.externalProgramId && (
              <Field
                label={t('programs.programOverview.tracking.postbackUrl')}
                helperText={t('programs.programOverview.tracking.postbackUrlHint')}
              >
                <Input
                  readOnly
                  type={postbackVisible ? 'text' : 'password'}
                  value={
                    postbackVisible ? (postbackUrl ?? '') : (settings.postback.redactedUrl ?? '')
                  }
                  placeholder={t('programs.programOverview.tracking.postbackUrlPlaceholder')}
                  autoComplete="off"
                  fontFamily="mono"
                  fontSize="xs"
                />
                <Flex gap={2} wrap="wrap" marginTop={2}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void togglePostbackVisibility()}
                    loading={credentialMutation.isPending}
                  >
                    {postbackVisible ? (
                      <EyeClosed width={17} height={17} />
                    ) : (
                      <Eye width={17} height={17} />
                    )}
                    {t(
                      postbackVisible
                        ? 'programs.programOverview.tracking.hidePostback'
                        : 'programs.programOverview.tracking.showPostback'
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void copyCredential()}
                    loading={credentialMutation.isPending}
                  >
                    <Copy width={17} height={17} />
                    {t('programs.programOverview.tracking.copyPostback')}
                  </Button>
                </Flex>
              </Field>
            )}
          </VStack>
        )}
      </Card.Body>
      <ConfirmDialog
        isOpen={confirmingProgramIdChange}
        title={t('programs.programOverview.tracking.confirmProgramIdChangeTitle')}
        description={t('programs.programOverview.tracking.confirmProgramIdChangeDescription', {
          previousProgramId: preview?.previousExternalProgramId ?? '—',
          nextProgramId: preview?.externalProgramId ?? '—',
        })}
        confirmText={t('programs.programOverview.tracking.confirmProgramIdChange')}
        confirmColorPalette="red"
        isLoading={updateMutation.isPending}
        onCancel={() => setConfirmingProgramIdChange(false)}
        onConfirm={() => {
          if (preview?.previousExternalProgramId) {
            void save(preview.previousExternalProgramId);
          }
        }}
      />
    </Card.Root>
  );
};

export default ProgramTrackingCard;
